import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../constants/firebaseModular';
import { scrubSafeAuthCode } from '../utils/mfa/sentryAuthCode';

/**
 * Is the account email verified? — CU-86akjgbyw (part 3 of CU-86akjgbpk).
 *
 * ── ⚠️ WHY THE GATE ASKS THIS AT ALL ──────────────────────────────────────
 *
 * Verifying an email bumps `tokensValidAfterTime`. The H4 staleness check
 * (`passkeyVerify.ts:321-327`) then sees `binding.createdAt < validAfterMs`,
 * rejects the assertion and **DELETES the credential** — fire-and-forget, no
 * audit row, no notification. Reproduced 2026-09-15.
 *
 * Identity Platform refuses SMS enrollment until the email is verified
 * (`manageAccountVerification.ts:9`), so an unverified patient at the gate can
 * complete ONLY a passkey — and verifying afterwards destroys it. The gate
 * offers passkey FIRST, as Tier 0. That is the trap, and it is the common path.
 *
 * Hiding BOTH factors until the email is verified makes the damaging order
 * UNREACHABLE rather than merely discouraged. This hook is the read that
 * decides it.
 *
 * ── ⚠️ NOT A SECURITY BOUNDARY, AND MUST NOT BECOME ONE ───────────────────
 *
 * The server is the authority — IdP enforces the SMS rule itself, and it always
 * did. This is a client-side preflight: it saves a doomed round-trip and, far
 * more importantly, lets us WARN before the patient destroys their own
 * credential. A fault here must therefore FALL THROUGH, never block. See
 * `recheck`.
 *
 * ── ⚠️ WHY A SEPARATE HOOK AND NOT `useSmsEnrollment` ─────────────────────
 *
 * `useSmsEnrollment` owns the same two behaviours (the verified preflight at
 * `:565-596`, the resend at `:709-744`) and they are ported here deliberately
 * rather than shared:
 *
 *   • It is a ~900-line MFA CEREMONY hook on the repo's safety-rail list. The
 *     gate needs two of its behaviours and none of its reCAPTCHA/enroll
 *     machinery, and mounting it here to borrow a resend would drag the
 *     verifier lifecycle onto a surface that must never render one.
 *   • Extracting a shared util would mean editing that file inside a Tier 1
 *     change. Considered and rejected 2026-09-16; revisit as cleanup, not here.
 *
 * ⚠️ THE COST IS DRIFT, AND IT IS REAL. Two resends now exist in this repo.
 * `useSmsEnrollment.resendVerificationEmail` is the SMS-step Layer-2 backstop
 * for a server-side rejection and is deliberately KEPT (Q3) — unreachable in
 * the common path is not the same as dead. If you change the classification
 * rules in one, change them in the other. Both carry this note.
 */

/** What we know about the account email. */
export type EmailVerificationState =
  /** The read has not completed yet. ⚠️ NOT "unverified" — see `unknown`. */
  | 'checking'
  /** Confirmed verified. Factors may be offered. */
  | 'verified'
  /** Confirmed unverified. Neither factor may be offered. */
  | 'unverified'
  /**
   * ⚠️ THE READ FAULTED — and this does NOT block, deliberately.
   *
   * A reload can fail for reasons that have nothing to do with the patient
   * (offline, token refresh hiccup, IdP blip). Blocking on it would deny a
   * VERIFIED patient both factors at a fail-closed gate, which is a lockout
   * from their own PHI caused by our own network.
   *
   * ⚠️ THIS IS A KNOWING TRADE, NOT A COSTLESS ONE — CORRECTED 2026-09-16 after
   * an adversarial round. This comment previously claimed the fall-through
   * "costs nothing that was not already enforced one layer down." ⚠️ THAT WAS
   * TRUE FOR SMS AND FALSE FOR PASSKEYS, and the difference is the whole defect:
   *
   *   • SMS — Identity Platform refuses enrollment until `emailVerified`
   *     (`auth/mfa/manageAccountVerification.ts:9`). A real second layer.
   *   • PASSKEY — ⚠️ THERE IS NO SECOND LAYER. Measured in the backend repo:
   *     `grep -rn "emailVerified" src/auth/passkey/` returns ZERO matches
   *     across all 25 files, and `passkeyRegister.ts` never reads it (every
   *     "verified" there is WebAuthn attestation, not email).
   *
   * So an unverified patient reaching `unknown` CAN complete a passkey, and the
   * gate offers passkey FIRST — which is a narrow reopening of the exact H4
   * order this task exists to close, gated on a network fault rather than on
   * anything the patient does.
   *
   * ⚠️ WE ACCEPT IT, AND WE MITIGATE IT. Blocking instead would strand a
   * verified patient on a blip, which is worse and more common. The mitigation
   * is at the call site: `MfaGateFactorChoice` keeps the passkey-destruction
   * WARNING visible on this path, so the patient who might lose a credential is
   * still told even though the factors are offered.
   *
   * ⚠️ The durable fix is server-side — an `emailVerified` guard in
   * `passkeyRegister` — which is auth logic on the passkey security boundary
   * and needs its own task and its own adversarial review. Do not add it here.
   */
  | 'unknown';

export interface EmailVerificationGate {
  state: EmailVerificationState;
  /**
   * ⚠️ The ONLY state that may hide the factor choice. `unknown` must not —
   * see the union above. Derived here so no call site re-derives it wrongly.
   */
  blocksEnrollment: boolean;
  /** True while a recheck is in flight, for button `working` state. */
  busy: boolean;
  /** Re-read from Identity Platform. ⚠️ `reload()` first — see below. */
  recheck: () => Promise<void>;
  /** Re-send the verification email. Returns false on any failure. */
  resend: () => Promise<boolean>;
}

/** Injectable seams. ⚠️ Test-only — app code passes nothing. */
export interface EmailVerificationGateDeps {
  reloadUser?: () => Promise<void>;
  isEmailVerified?: () => boolean | undefined;
  sendVerificationEmail?: () => Promise<void>;
}

export function useEmailVerificationGate(
  deps: EmailVerificationGateDeps = {},
): EmailVerificationGate {
  const [state, setState] = useState<EmailVerificationState>('checking');
  const [busy, setBusy] = useState(false);

  /**
   * ⚠️ READ THE STATE, NEVER THE CACHED SNAPSHOT.
   *
   * `emailVerified` on the cached user object is a SNAPSHOT FROM TOKEN-MINT
   * TIME. A patient who verifies in their inbox and comes straight back still
   * carries a stale `false`. So: `reload()` FIRST, then read — the discipline
   * proven at `useSmsEnrollment.ts:565-596` and asserted there by an ORDER test
   * (`expect(order).toEqual(['reload', 'read'])`), not an occurrence test.
   *
   * ⚠️ A RELOAD FAULT RESOLVES TO `unknown`, WHICH DOES NOT BLOCK. Falling
   * through is the correct failure direction at a fail-closed gate; see the
   * `unknown` docs above.
   */
  const read = useCallback(async (): Promise<EmailVerificationState> => {
    const user = auth.currentUser;
    /**
     * ⚠️ NO SESSION IS NOT THE SAME AS A NETWORK FAULT — adversarial round F2.
     *
     * Both resolve to `unknown` and therefore both fall through, which is the
     * right call here: this gate renders only inside `MfaRouteGate`'s `enroll`
     * branch, behind the terms acknowledgement and a resolved `patientDoc`, so
     * a null user at mount is already an odd state — and a genuinely signed-out
     * patient fails at the server regardless. Blocking would add a lockout path
     * without adding security.
     *
     * ⚠️ WHAT IS NOT ACCEPTABLE is latching that answer forever. If auth
     * restores asynchronously after a null mount read, the old latch left the
     * gate permanently fell-through for that mount. The effect below re-arms on
     * a null → non-null transition for exactly this reason.
     */
    if (!user) return 'unknown';

    try {
      if (deps.reloadUser) {
        await deps.reloadUser();
      } else {
        await user.reload();
      }
    } catch (reloadError) {
      // ⚠️ A BREADCRUMB, NOT A CAPTURE. A failed reload is an ordinary network
      // event on a screen every gated patient sees; capturing it would bury the
      // genuine faults under offline noise. Same classification as the SMS
      // preflight's reload.
      Sentry.addBreadcrumb({
        category: 'mfa-email-verification',
        level: 'warning',
        message: 'gate: user.reload() failed, treating as unknown',
        data: { code: scrubSafeAuthCode((reloadError as { code?: string })?.code) },
      });
      return 'unknown';
    }

    const verified = deps.isEmailVerified
      ? deps.isEmailVerified()
      : auth.currentUser?.emailVerified;

    // ⚠️ `undefined` IS NOT `false`. An absent value means we could not read it
    // — the user object went away mid-flight — and that is `unknown`, which
    // does not block. Matching the `!== false` convention in the SMS preflight.
    if (verified === undefined) return 'unknown';
    return verified ? 'verified' : 'unverified';
  }, [deps]);

  /**
   * ⚠️ ONE READ ON MOUNT, GUARDED BY A REF — NOT ONE PER RENDER.
   *
   * `reload()` is a NETWORK CALL. The expensive prior art on this very
   * component tree: an inline arrow in `handleSmsComplete` minted a new
   * function identity every render, re-firing a completion effect and driving
   * **20,379 requests** to `securetoken.googleapis.com` from a single
   * enrolment (staging, 2026-09-02).
   *
   * ⚠️ `deps` IS DELIBERATELY NOT IN THE DEPENDENCY ARRAY. Callers construct it
   * inline, so it is a new object identity on every render — including it would
   * re-run this effect forever, which is the same unstable-identity family that
   * has now cost this workstream four separate defects. The ref makes the
   * intent explicit rather than relying on the lint suppression alone.
   */
  const readStartedRef = useRef(false);
  /**
   * ⚠️ RE-ARMS ON A NULL → NON-NULL USER TRANSITION — adversarial round F2.
   *
   * Without this, a null `auth.currentUser` at mount latched `unknown` for the
   * life of the mount: auth restoring a moment later produced ZERO further
   * reads, so the gate stayed permanently fall-through for a patient whose
   * email we could have checked. Measured before the fix: `reloads: 0`.
   *
   * ⚠️ ONE EXTRA READ AT MOST, NOT A POLL. It re-arms on the TRANSITION only —
   * a uid that was absent and is now present. It cannot re-fire while the uid
   * is stable, which is what keeps `reload()` off the render path.
   */
  const lastUidRef = useRef<string | null>(auth.currentUser?.uid ?? null);
  const currentUid = auth.currentUser?.uid ?? null;
  useEffect(() => {
    if (lastUidRef.current === null && currentUid !== null) {
      readStartedRef.current = false;
    }
    lastUidRef.current = currentUid;

    if (readStartedRef.current) return;
    readStartedRef.current = true;
    void (async () => {
      setState(await read());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid]);

  /**
   * An explicit recheck — driven by a BUTTON PRESS, never by a render.
   *
   * This is the patient's route out: they verify in their inbox, come back, and
   * press it. Without it they would have to guess that a full page reload is
   * what re-reads the token.
   */
  const inFlightRef = useRef(false);
  const recheck = useCallback(async (): Promise<void> => {
    // ⚠️ A REF, NOT `busy` — adversarial round F4. `setBusy(true)` disables the
    // button, but React state updates are async, so clicks landing in the same
    // tick sail past it. Measured before this guard: three rapid clicks issued
    // three concurrent reloads. Harmless (the reads are idempotent and Firebase
    // throttles) but it is free to not do it.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    try {
      setState(await read());
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }, [read]);

  /**
   * Re-send the account verification email.
   *
   * ⚠️ PORTED FROM `useSmsEnrollment.ts:709-744` WITH ITS CLASSIFICATION INTACT.
   * Preserve both halves:
   *
   *   • `auth/too-many-requests` is a BREADCRUMB, not a capture. Firebase
   *     rate-limits verification sends and a patient clicking twice is not a
   *     fault. Capturing it would make an ordinary double-press look like an
   *     incident.
   *   • ANYTHING ELSE IS A CAPTURE, because it means the patient's ONLY route
   *     out of the unverified state is broken — and the Firebase console has NO
   *     admin-side "send verification email" action, so support cannot do it
   *     for them. A silent failure here is a permanent lockout.
   *
   * ⚠️ RETURNS A BOOLEAN RATHER THAN THROWING, and deliberately does not touch
   * `state`. The caller shows a transient "sent" / "couldn't send" ALONGSIDE the
   * standing unverified copy — which carries the instruction the patient still
   * needs. The SMS-step equivalent makes the same choice for the same reason.
   */
  const resend = useCallback(async (): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      if (deps.sendVerificationEmail) {
        await deps.sendVerificationEmail();
      } else {
        await sendEmailVerification(user);
      }
      Sentry.addBreadcrumb({
        category: 'mfa-email-verification',
        level: 'info',
        message: 'gate: verification email sent',
      });
      return true;
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      if (code === 'auth/too-many-requests') {
        Sentry.addBreadcrumb({
          category: 'mfa-email-verification',
          level: 'warning',
          message: 'gate: verification email throttled',
          data: { code: scrubSafeAuthCode(code) },
        });
      } else {
        Sentry.captureException(err, {
          tags: {
            feature: 'mfa-email-verification',
            step: 'gate-verification-email',
            auth_error_code: scrubSafeAuthCode(code),
          },
        });
      }
      return false;
    }
  }, [deps]);

  /**
   * ⚠️ `recheck` AND `resend` HAVE UNSTABLE IDENTITY EVERY RENDER — measured,
   * adversarial round (2026-09-16). Callers pass `deps` inline, so it defaults
   * to a fresh `{}` object, which re-creates both callbacks.
   *
   * ⚠️ HARMLESS TODAY, A LANDMINE TOMORROW. Nothing puts either in an effect
   * dependency array, and the gate's telemetry effect depends only on
   * primitives. But this is the "unstable callback identity" family that has
   * cost this workstream four defects, including the 20,379-request loop. If
   * you need one in a dep array, memoise `deps` at the CALL SITE first —
   * do not add it to a dep array as-is.
   */
  return {
    state,
    // ⚠️ ONLY a CONFIRMED `unverified` blocks. `checking` renders its own
    // waiting view and `unknown` falls through to the factor choice — see the
    // state union. Derived here so no call site can get this wrong.
    blocksEnrollment: state === 'unverified',
    busy,
    recheck,
    resend,
  };
}
